<template>
  <div v-b-tooltip.hover.bottom.html="tooltip" class="password-strength">
    <div :class="passwordStrength.score > 3 ? 'strength-' + passwordStrength.score : ''" class="password-input-dot" />
    <div :class="passwordStrength.score > 2 ? 'strength-' + passwordStrength.score : ''" class="password-input-dot" />
    <div :class="passwordStrength.score > 1 ? 'strength-' + passwordStrength.score : ''" class="password-input-dot" />
    <div :class="passwordStrength.score >= 0 ? 'strength-' + passwordStrength.score : ''" class="password-input-dot" />
  </div>
</template>

<script>
/**
 * To not load zxcvbn at startup, this component throw event to be used as validator
 * @weak event will be thrown when password is not strong enough (score < 3)
 * @strong event will be thrown when password is strong enough (score >= 3)
 */
import { upperFirst, debounce } from 'lodash-es'
import to from '@/modules/await-to-js'

import vBTooltip from 'bootstrap-vue/es/directives/tooltip/tooltip'

export default {
  directives: { 'b-tooltip': vBTooltip },

  props: {
    password: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      passwordStrength: {}
    }
  },

  computed: {
    tooltip () {
      let message = ''
      if (this.password) message += '<strong>' + upperFirst(this.$t('passwordStrength:' + this.passwordStrength.label)) + '</strong><br>'
      message += upperFirst(this.$t('passwordStrength:helper'))
      return message
    }
  },

  watch: {
    password () {
      this.getPasswordStrength()
    },

    passwordStrength () {
      let minStrength = 3

      // Deactivate min strength in dev
      if (this.$store.getters.application && this.$store.getters.application.license) minStrength = this.$store.getters.application.license.official ? 3 : 0

      if (this.password === '') this.$emit('reset')
      if (this.password !== '' && this.passwordStrength.score < minStrength) this.$emit('weak')
      if (this.password !== '' && this.passwordStrength.score >= minStrength) this.$emit('strong')
    }
  },

  methods: {
    getPasswordStrength: debounce(async function () {
      let authToken = null
      if (this.$route.name === 'resetPassword' && this.$route.query) authToken = this.$route.query.t

      let error, response, strength
      [error, response] = await to(this.$api.getPasswordStrength(this.$store.getters.application, this.password, authToken))

      if (error || !response) strength = -1
      else strength = response.data

      switch (strength) {
        case 0:
          this.passwordStrength = { score: strength, label: 'weak' }
          break
        case 1:
          this.passwordStrength = { score: strength, label: 'weak' }
          break
        case 2:
          this.passwordStrength = { score: strength, label: 'notBad' }
          break
        case 3:
          this.passwordStrength = { score: strength, label: 'good' }
          break
        case 4:
          this.passwordStrength = { score: strength, label: 'great' }
          break
        default:
          this.passwordStrength = { score: -1 }
          break
      }
    }, 300)
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  .password-strength {
    position: absolute;
    top: 2rem;
    right: 1px;
    display: block;
    padding: 0 15px;
    font-size: 0.65rem;
    cursor: pointer;
    background-color: $white;

    .password-input-dot {
      width: 5px;
      height: 5px;
      margin: 2px;
      background-color: $gray-300;

      &.strength-0,
      &.strength-1 {
        background-color: $danger;
      }

      &.strength-2 {
        background-color: $warning;
      }

      &.strength-3,
      &.strength-4 {
        background-color: $success;
      }
    }
  }
</style>
